<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import ContentsTitle from './ContentsTitle.vue';

const props = defineProps({
  userName: { type: String, default: '' },
  isMobile: { type: Boolean, default: false },
  data: { type: Object, default: () => ({}) },
});

// 데이터 접근 (props.data.운세해석 = 3_운세해석)
const 운세해석 = computed(() => props.data?.운세해석 || {});
const 기본운세 = computed(() => 운세해석.value?.기본운세 || {});
const 개념특징 = computed(() => 기본운세.value?.개념특징 || {});
const 주요강점 = computed(() => 기본운세.value?.주요강점 || {});

// 섹션1 데이터 (파란색 - 핵심 구조)
const 특징요약 = computed(() => {
  const raw = 개념특징.value?.특징요약 || '';
  return raw ? raw.split(', ') : [];
});
const 개념설명 = computed(() => transformYearlyFortuneText(개념특징.value?.개념 || ''));
const 특징상세 = computed(() => transformYearlyFortuneText(개념특징.value?.특징 || ''));

// 섹션2 데이터 (초록색 - 성장 포인트)
const 강점요약 = computed(() => {
  const raw = 주요강점.value?.강점요약 || '';
  return raw ? raw.split(', ') : [];
});
const 강점설명 = computed(() => transformYearlyFortuneText(주요강점.value?.강점설명 || ''));
const 강점추천 = computed(() => transformYearlyFortuneText(주요강점.value?.강점추천 || ''));

// 오른쪽 컬럼 데이터
const 주요약점 = computed(() => 기본운세.value?.주요약점 || {});
// 변화특징은 기본운세 안에 있거나 운세해석 바로 아래에 있을 수 있음
const 변화특징 = computed(() => 기본운세.value?.변화특징 || 운세해석.value?.변화특징 || {});

// 섹션3 데이터 (빨간색 - 조심하면 좋은 부분)
const 약점요약 = computed(() => {
  const raw = 주요약점.value?.약점요약 || '';
  return raw ? raw.split(', ') : [];
});
const 약점설명 = computed(() => {
  return transformYearlyFortuneText(주요약점.value?.약점설명 || '');
});
const 약점추천 = computed(() => transformYearlyFortuneText(주요약점.value?.약점추천 || ''));

// 섹션4 데이터 (노란색 - 변화의 특징)
const 변화분석 = computed(() => {
  return transformYearlyFortuneText(변화특징.value?.변화분석 || '');
});

// 섹션5 데이터 (보라색 - 명리적 변화 작용)
const 변화상세 = computed(() => {
  const raw = 변화특징.value?.변화상세 || [];
  return raw.flatMap((item) => item.split(' • '));
});
const isChangeDetailOpen = ref(false);
</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <!-- 타이틀 -->
        <ContentsTitle
          :number="3"
          text="명리로 짚어보는 2026년, 나의 흐름 구조"
          :isMobile="props.isMobile"
        />

        <div class="structure-wrapper" :class="{ mobile: props.isMobile }">
          <!-- 섹션1: 올해 작용하는 흐름의 핵심 구조 -->
          <section class="structure-section">
            <div class="structure-section__title">
              <span>🧭</span>
              <span>올해 작용하는 흐름의 핵심 구조</span>
            </div>

            <!-- 원형 키워드 (파란색) -->
            <div class="guide__circle-box" :class="{ mobile: props.isMobile }">
              <div class="circle-box__overlap-group">
                <div
                  v-for="(item, idx) in 특징요약.slice(0, 3)"
                  :key="`blue-circle-${idx}`"
                  class="structure-circle structure-circle--blue"
                  :class="{ 'overlap-circle': idx > 0, light: idx === 1 }"
                >
                  <span>{{ item }}</span>
                </div>
              </div>
            </div>

            <!-- 설명 카드 (파란색) -->
            <div class="structure-card structure-card--blue">
              <p class="structure-card__desc" v-html="개념설명"></p>

              <div class="structure-card__detail">
                <p class="structure-card__detail-title">그래서 이렇게 나타나요!</p>
                <p class="structure-card__detail-text" v-html="특징상세"></p>
              </div>
            </div>
          </section>

          <!-- 구분선 -->
          <div class="structure-divider"></div>

          <!-- 섹션2: 이 흐름이 주는 성장 포인트 -->
          <section class="structure-section">
            <div class="structure-section__title">
              <span>🌱</span>
              <span>이 흐름이 주는 성장 포인트</span>
            </div>

            <!-- 원형 키워드 (초록색) -->
            <div class="guide__circle-box" :class="{ mobile: props.isMobile }">
              <div class="circle-box__overlap-group">
                <div
                  v-for="(item, idx) in 강점요약.slice(0, 3)"
                  :key="`green-circle-${idx}`"
                  class="structure-circle structure-circle--green"
                  :class="{ 'overlap-circle': idx > 0, light: idx === 1 }"
                >
                  <span>{{ item }}</span>
                </div>
              </div>
            </div>

            <!-- 설명 카드 (초록색) -->
            <div class="structure-card structure-card--green">
              <p class="structure-card__desc" v-html="강점설명"></p>

              <div class="structure-card__detail">
                <p class="structure-card__detail-title">추천</p>
                <p class="structure-card__detail-text" v-html="강점추천"></p>
              </div>
            </div>
          </section>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="structure-wrapper" :class="{ mobile: props.isMobile }">
          <!-- 섹션3: 조심하면 좋은 부분 -->
          <section class="structure-section">
            <div class="structure-section__title">
              <span>🛡</span>
              <span>조심하면 좋은 부분</span>
            </div>

            <!-- 원형 키워드 (빨간색) -->
            <div class="guide__circle-box" :class="{ mobile: props.isMobile }">
              <div class="circle-box__overlap-group">
                <div
                  v-for="(item, idx) in 약점요약.slice(0, 3)"
                  :key="`red-circle-${idx}`"
                  class="structure-circle structure-circle--red"
                  :class="{ 'overlap-circle': idx > 0, light: idx === 1 }"
                >
                  <span>{{ item }}</span>
                </div>
              </div>
            </div>

            <!-- 설명 카드 (빨간색) -->
            <div class="structure-card structure-card--red">
              <p class="structure-card__desc" v-html="약점설명"></p>

              <div class="structure-card__detail">
                <p class="structure-card__detail-title">추천</p>
                <p class="structure-card__detail-text" v-html="약점추천"></p>
              </div>
            </div>
          </section>

          <!-- 구분선 -->
          <div class="structure-divider"></div>

          <!-- 섹션4: 올해 나타나는 변화의 특징 -->
          <section class="structure-section">
            <div class="structure-section__title">
              <span>💡</span>
              <span>올해 나타나는 변화의 특징</span>
            </div>

            <!-- 설명 카드 (노란색) -->
            <div class="structure-card structure-card--yellow">
              <p class="structure-card__desc" v-html="변화분석"></p>
            </div>
          </section>

          <!-- 구분선 -->
          <div class="structure-divider"></div>

          <!-- 섹션5: 명리적으로 보는 변화의 작용 (접기/펼치기) -->
          <section class="structure-section">
            <div class="structure-card structure-card--purple">
              <div class="structure-card__header" @click="isChangeDetailOpen = !isChangeDetailOpen">
                <div class="structure-card__header-title">
                  <span>🔮</span>
                  <span>명리적으로 보는 변화의 작용</span>
                </div>
                <div class="structure-card__header-toggle" :class="{ open: isChangeDetailOpen }">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="9.5" fill="#fff" stroke="#ebebeb" />
                    <path
                      d="M7 9L10 12L13 9"
                      stroke="#333"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <div v-if="isChangeDetailOpen" class="structure-card__list">
                <div
                  v-for="(item, idx) in 변화상세"
                  :key="`change-detail-${idx}`"
                  class="structure-card__list-item"
                >
                  <span class="bullet">•</span>
                  <span v-html="transformYearlyFortuneText(item)"></span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
// 컨텐츠 래퍼
.structure-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  padding-bottom: 130px;
  gap: 24px;

  &.mobile {
    padding: 24px 0;
    padding-bottom: 24px;
    gap: 26px;
  }
}

// 섹션
.structure-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;

  &__title {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    width: 100%;
  }

  .structure-wrapper.mobile & {
    gap: 20px;
  }
}

// 섹션 타이틀 모바일
.structure-wrapper.mobile .structure-section__title {
  padding: 0 16px;
}

// 구분선
.structure-divider {
  width: 100%;
  height: 1px;
  background: #ebebeb;

  .structure-wrapper.mobile & {
    margin: 0 16px;
    width: calc(100% - 32px);
  }
}

// 원형 박스 (guide__circle-box 참조)
.guide__circle-box {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .circle-box__overlap-group {
    display: flex;
    padding-right: 12px;

    .overlap-circle {
      margin-left: -12px;
    }
  }

  &.mobile {
    .structure-circle {
      width: 100px;
      height: 100px;

      span {
        font-size: 14px;
      }
    }
  }
}

// 원형 스타일
.structure-circle {
  display: flex;
  width: 120px;
  height: 120px;
  padding: 20px;
  justify-content: center;
  align-items: center;
  border-radius: 200px;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: transform 0.2s ease;
  animation: circlePop 0.4s ease-out backwards;

  &:hover {
    transform: scale(1.08);
  }

  // 순차적 등장
  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  span {
    color: inherit;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: center;
  }

  &--blue {
    border: 1px solid rgba(87, 144, 252, 0.5);
    color: #5790fc;

    &.light {
      background: rgba(87, 144, 252, 0.05);
    }
  }

  &--green {
    border: 1px solid rgba(136, 195, 74, 0.5);
    color: #88c34a;

    &.light {
      background: rgba(136, 195, 74, 0.05);
    }
  }

  &--red {
    border: 1px solid rgba(255, 110, 110, 0.5);
    color: #ff6e6e;

    &.light {
      background: rgba(255, 110, 110, 0.05);
    }
  }
}

@keyframes circlePop {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  70% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

// 카드 스타일
.structure-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  width: 100%;

  &--blue {
    background: #f3f7ff;
  }

  &--green {
    background: rgba(136, 195, 74, 0.05);
  }

  &--red {
    background: #fff5f5;
  }

  &--yellow {
    background: #fefbf3;
  }

  &--purple {
    background: #f7f5ff;
    gap: 16px;
  }

  &__desc {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
  }

  &__detail {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.8);
  }

  &__detail-title {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    margin: 0;
  }

  &__detail-text {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
  }

  // 모바일 스타일
  .structure-wrapper.mobile & {
    border-radius: 0;
    padding: 20px 16px;
  }

  // 접기/펼치기 헤더
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    cursor: pointer;
  }

  &__header-title {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__header-toggle {
    transition: transform 0.2s ease;

    &.open {
      transform: rotate(180deg);
    }
  }

  // 리스트 스타일
  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  &__list-item {
    display: flex;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.8);
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;

    .bullet {
      flex-shrink: 0;
    }
  }
}

// 카드 모바일 텍스트 스타일
.structure-wrapper.mobile .structure-card__desc {
  font-size: 14px;
  line-height: 22px;
}

.structure-wrapper.mobile .structure-card__detail-text {
  font-size: 14px;
  line-height: 22px;
}

.structure-wrapper.mobile .structure-card__list-item {
  font-size: 14px;
  line-height: 22px;
}
</style>
